"use client";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import Image from "next/image";
import { ArrowRightIcon } from "lucide-react";
import Link from "next/link";
import { motion } from "framer-motion";
import { Fragment } from "react";

type CardProps = {
  item: {
    name: string;
    images?: { alt: string; src: string }[];
    description?: (string | string[])[];
    [key: string]: any;
  };
  idx: number;
  fullWidth?: boolean;
};

function InnerCard(
  props: CardProps & { noBullets?: boolean; cover?: boolean }
) {
  return (
    <Card
      className={cn(
        "w-full bg-sima-grey-dark flex relative h-full border-none p-4 min-w-[300px] md:min-w-[350px] lg:min-w-[400px]"
      )}
    >
      <CardHeader
        className={cn(
          "text-zinc-50 text-3xl lg:text-4xl uppercase font-sans bg-sima-orange inline-flex -translate-y-12 translate-x-4 absolute p-2 z-10 font-bold"
        )}
      >
        {props.item.name}
      </CardHeader>
      <CardContent
        className={cn(
          "w-full flex p-2 md:p-4 items-center justify-between flex-1",
          props.fullWidth ? "md:flex-row flex-col" : "flex-col"
        )}
      >
        {props.item.images && props.item.images.length > 0 && (
          <div
            className={cn(
              "flex flex-row items-center gap-2",
              props.fullWidth
                ? "w-full md:w-1/2 lg:w-1/3 md:h-full aspect-square "
                : "w-full"
            )}
          >
            {props.item.images.map((img) => (
              <div
                key={img.alt}
                className={cn(
                  // "relative md:h-[240px] lg:h-[320px] py-4 w-full aspect-square",
                  "relative mx-auto py-4 w-full aspect-square",
                  props.fullWidth
                    ? "max-w-[600px]  lg:aspect-square"
                    : "aspect-square"
                )}
              >
                <Image
                  src={img.src}
                  alt={img.alt}
                  fill
                  sizes={"20vw"}
                  quality={100}
                  className={cn(
                    "w-full p-4",
                    props.cover ? "object-cover" : "object-contain"
                  )}
                />
              </div>
            ))}
          </div>
        )}

        {props.item.description ? (
          <ul
            className={cn(
              " w-5/6 mx-auto text-md md:text-lg lg:text-xl text-zinc-50 md:p-2 flex-1 ",
              props.noBullets
                ? "list-none"
                : "list-disc marker:text-sima-orange"
            )}
          >
            {props.item.description.map((desc) =>
              typeof desc === "string" ? (
                <li key={desc} className="py-2 leading-8 md:leading-10">
                  {desc}
                </li>
              ) : (
                <Fragment key={desc[0]}>
                  <p>{desc[0]}</p>
                  <ul className="list-disc px-8 py-2 marker:text-sima-orange">
                    {desc.map((item, idx) =>
                      idx !== 0 ? <li key={item}>{item}</li> : null
                    )}
                  </ul>
                </Fragment>
              )
            )}
          </ul>
        ) : null}
        {props.item.link ? (
          <div className="w-full flex items-center justify-end text-right group py-4 self-end">
            <Link
              href={props.item.link.href}
              className=" text-zinc-50 text-lg font-sans flex flex-row items-center group-hover:text-sima-orange font-bold transition-colors ease-out duration-500"
            >
              {props.item.link.text}
              <ArrowRightIcon className="h-6 w-6 ml-2 group-hover:translate-x-2 text-zinc-50 transition-transform group-hover:text-sima-orange ease-out duration-500" />
            </Link>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
export function ItemCard(
  props: CardProps & {
    noMotion?: boolean;
    noBullets?: boolean;
    cover?: boolean;
  }
) {
  return props.noMotion ? (
    <InnerCard {...props} />
  ) : (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{
        ease: "easeIn",
        delay: 0,
        duration: 0.2,
      }}
    >
      <InnerCard {...props} />
    </motion.div>
  );
}
